import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight,
  ArrowRight,
  Sun,
  Handshake,
  Zap,
  Network,
  ShieldCheck,
  Clock,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ContactForm } from "@/components/ContactForm";
import { useLenis } from "@/hooks/useLenis";

const HERO_IMG =
  "https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwY2xlYW4lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzgzOTk3NzI2fDA&ixlib=rb-4.1.0&q=85";
const SOLAR_IMG =
  "https://images.unsplash.com/photo-1761472823286-9f6093ed6663?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVscyUyMG1vZGVybiUyMHJvb2Z8ZW58MHx8fHwxNzgzOTk3NzI2fDA&ixlib=rb-4.1.0&q=85";
const BROKER_IMG =
  "https://images.pexels.com/photos/8112186/pexels-photo-8112186.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const APPS_IMG =
  "https://images.unsplash.com/photo-1558655146-364adaf1fcc9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMG1vYmlsZSUyMGFwcCUyMGRldmVsb3BtZW50fGVufDB8fHx8MTc4Mzk5NzcyNnww&ixlib=rb-4.1.0&q=85";

const EASE = [0.16, 1, 0.3, 1];

/* ---- masked line reveal ---- */
const HeroLine = ({ children, delay = 0 }) => (
  <span className="line-mask">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

/* ---- generic scroll reveal ---- */
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 34 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

const spotlightMove = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--x", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--y", `${e.clientY - r.top}px`);
};

const marqueeWords = [
  "Solar & Battery",
  "Essential Services",
  "Electrical Estimation",
  "Subcontractor Networking",
  "Independent Advice",
];

const chapters = [
  {
    n: "01",
    title: "Independent by design",
    body: "We answer to you — not to suppliers. Every recommendation is impartial, so the outcome is always in your interest.",
  },
  {
    n: "02",
    title: "Vetted, not guessed",
    body: "Providers and installers are screened for quality, price and reliability before a single quote reaches you.",
  },
  {
    n: "03",
    title: "Built with software",
    body: "We're engineering tools that make quoting, estimating and switching essential services genuinely effortless.",
  },
];

const apps = [
  { icon: Zap, name: "Electrical Estimation", desc: "Fast, accurate electrical estimating for trades." },
  { icon: Network, name: "Subcontractor Networking", desc: "Connecting subcontractors with the right jobs." },
];

export default function Landing() {
  useLenis();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <div className="min-h-screen bg-paper text-foreground selection:bg-primary selection:text-primary-foreground">
      <Navbar />

      {/* ============ HERO ============ */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-end overflow-hidden">
        <div className="absolute inset-0 dotted-grid opacity-70" aria-hidden />

        {/* Parallax framed image, right side */}
        <motion.div
          style={{ y: imgY }}
          className="absolute right-0 top-0 h-full w-full lg:w-[46%] overflow-hidden"
          aria-hidden
        >
          <motion.img
            style={{ scale: imgScale }}
            src={HERO_IMG}
            alt=""
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-primary/25 mix-blend-multiply" />
          <div className="absolute inset-0 bg-gradient-to-r from-paper via-paper/10 to-transparent lg:from-paper/90" />
        </motion.div>

        <motion.div style={{ y: textY }} className="relative mx-auto max-w-[1400px] w-full px-6 lg:px-12 pb-20 lg:pb-28 pt-32">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase font-semibold text-primary"
          >
            <span className="h-px w-10 bg-accent-warm" />
            Energy · Trades · Essential Services
          </motion.div>

          <h1 className="mt-8 font-display font-medium tracking-tighter leading-[0.92] text-[16vw] sm:text-[13vw] lg:text-[9.5vw] max-w-[15ch]">
            <HeroLine delay={0.35}>Smarter</HeroLine>
            <HeroLine delay={0.47}>
              choices, <span className="text-primary italic">honest</span>
            </HeroLine>
            <HeroLine delay={0.59}>advice.</HeroLine>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
            className="mt-10 flex flex-col lg:flex-row lg:items-end gap-8 lg:gap-16"
          >
            <p className="max-w-md text-base sm:text-lg leading-relaxed text-muted-foreground text-balance">
              JLY Services is an independent brokerage and technology studio — helping
              households and trades compare solar &amp; battery quotes, connect
              essential services, and estimate with confidence.
            </p>
            <div className="flex items-center gap-4 shrink-0">
              <a
                href="#contact"
                data-testid="hero-cta-button"
                className="group inline-flex items-center gap-2 rounded-full bg-foreground text-background pl-6 pr-5 py-4 text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
              >
                Get a free quote
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a
                href="#services"
                data-testid="hero-secondary-button"
                className="inline-flex items-center gap-2 text-sm font-medium border-b border-foreground/30 pb-1 hover:border-foreground transition-colors duration-200"
              >
                Explore services
              </a>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ============ MARQUEE ============ */}
      <section className="relative border-y border-foreground/15 bg-ink text-background py-6 overflow-hidden" aria-hidden>
        <div className="marquee-track">
          {[0, 1].map((k) => (
            <span key={k} className="flex items-center">
              {marqueeWords.map((w) => (
                <span key={w + k} className="flex items-center font-display text-3xl sm:text-5xl font-medium tracking-tight px-8">
                  {w}
                  <span className="mx-8 text-accent-warm">✦</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </section>

      {/* ============ SERVICES ============ */}
      <section id="services" className="relative py-28 lg:py-40 scroll-mt-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <Reveal>
              <p className="text-[11px] tracking-[0.28em] uppercase font-semibold text-muted-foreground">
                / What we do
              </p>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium max-w-[14ch]">
                Two core services, one clear promise.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="max-w-xs text-muted-foreground leading-relaxed lg:text-right">
                Impartial guidance backed by vetted partners — with more offerings
                arriving soon.
              </p>
            </Reveal>
          </div>

          <div className="mt-16 grid gap-6 lg:grid-cols-12">
            {/* Solar — dominant */}
            <Reveal className="lg:col-span-7">
              <article
                data-testid="service-card-solar"
                onMouseMove={spotlightMove}
                className="spotlight group relative h-full overflow-hidden rounded-[28px] border border-border bg-card"
              >
                <div className="relative h-[300px] lg:h-[360px] overflow-hidden">
                  <img
                    src={SOLAR_IMG}
                    alt="Solar panels on a suburban roof"
                    className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-card via-card/10 to-transparent" />
                  <span className="absolute top-6 left-6 grid place-items-center h-12 w-12 rounded-full bg-accent-warm text-ink">
                    <Sun className="h-5 w-5" />
                  </span>
                  <span className="absolute top-7 right-7 font-display text-sm tracking-[0.2em] text-white/90">01</span>
                </div>
                <div className="p-8 lg:p-10">
                  <h3 className="font-display text-2xl lg:text-3xl font-medium tracking-tight">
                    Solar &amp; Battery Quoting
                  </h3>
                  <p className="mt-4 text-muted-foreground leading-relaxed max-w-xl">
                    Compare tailored solar and battery quotes from vetted installers.
                    We break down system size, savings and payback in plain language —
                    no jargon, no pressure.
                  </p>
                  <div className="mt-7 flex flex-wrap gap-2">
                    {["Tailored sizing", "Transparent pricing", "Vetted installers", "Payback estimates"].map((t) => (
                      <span key={t} className="rounded-full border border-border px-4 py-1.5 text-xs font-medium text-foreground/70">
                        {t}
                      </span>
                    ))}
                  </div>
                  <a href="#contact" data-testid="service-solar-cta" className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-primary group/link">
                    Request a quote
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                  </a>
                </div>
              </article>
            </Reveal>

            {/* Broker — supporting */}
            <Reveal delay={0.12} className="lg:col-span-5">
              <article
                data-testid="service-card-broker"
                onMouseMove={spotlightMove}
                className="spotlight group relative h-full overflow-hidden rounded-[28px] border border-border bg-ink text-background flex flex-col"
              >
                <div className="relative h-[220px] overflow-hidden">
                  <img
                    src={BROKER_IMG}
                    alt="Two people shaking hands"
                    className="h-full w-full object-cover opacity-90 transition-transform duration-[900ms] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
                  <span className="absolute top-6 left-6 grid place-items-center h-12 w-12 rounded-full bg-background text-ink">
                    <Handshake className="h-5 w-5" />
                  </span>
                  <span className="absolute top-7 right-7 font-display text-sm tracking-[0.2em] text-white/70">02</span>
                </div>
                <div className="p-8 lg:p-10 flex-1 flex flex-col">
                  <h3 className="font-display text-2xl lg:text-3xl font-medium tracking-tight">
                    Essential Services Broker
                  </h3>
                  <p className="mt-4 text-background/70 leading-relaxed flex-1">
                    We help you compare and connect essential services — energy,
                    telecoms and more — matching you to the right provider for your
                    situation.
                  </p>
                  <a href="#contact" data-testid="service-broker-cta" className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-accent-warm group/link">
                    Talk to us
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                  </a>
                </div>
              </article>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ ETHOS — numbered manifesto ============ */}
      <section id="ethos" className="relative py-28 lg:py-40 bg-ink text-background scroll-mt-20 overflow-hidden">
        <div className="absolute inset-0 dotted-grid opacity-20" aria-hidden />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-12">
          <Reveal>
            <p className="text-[11px] tracking-[0.28em] uppercase font-semibold text-accent-warm">
              / Our ethos
            </p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-[5.5rem] leading-[0.95] tracking-tighter font-medium max-w-[16ch]">
              A studio built on trust, not commission.
            </h2>
          </Reveal>

          <div className="mt-20 grid gap-px lg:grid-cols-3 border-t border-background/15">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.12}>
                <div className="lg:border-l border-background/15 lg:pl-10 pt-10 h-full" data-testid={`ethos-chapter-${c.n}`}>
                  <span className="font-display text-6xl lg:text-7xl font-medium text-background/25">{c.n}</span>
                  <h3 className="mt-6 font-display text-2xl font-medium tracking-tight">{c.title}</h3>
                  <p className="mt-4 text-background/65 leading-relaxed max-w-sm">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ APPS ============ */}
      <section id="apps" className="relative py-28 lg:py-40 scroll-mt-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid gap-16 lg:grid-cols-2 items-center">
          <Reveal>
            <p className="text-[11px] tracking-[0.28em] uppercase font-semibold text-muted-foreground">
              / In the workshop
            </p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium max-w-[12ch]">
              Products in development.
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-md">
              We're building software to make estimating and connecting trades
              effortless. Published apps will be linked here as they launch.
            </p>

            <div className="mt-10 space-y-4">
              {apps.map((a, i) => (
                <div
                  key={a.name}
                  data-testid="app-item"
                  className="group flex items-center gap-5 rounded-2xl border border-border bg-card px-6 py-5 hover:border-primary transition-colors duration-300"
                >
                  <span className="grid place-items-center h-12 w-12 rounded-full bg-accent text-primary shrink-0">
                    <a.icon className="h-5 w-5" />
                  </span>
                  <div className="flex-1">
                    <h3 className="font-display text-lg font-medium">{a.name}</h3>
                    <p className="text-sm text-muted-foreground">{a.desc}</p>
                  </div>
                  <span className="rounded-full bg-muted px-3 py-1 text-[11px] font-semibold tracking-wide uppercase text-muted-foreground">
                    Soon
                  </span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div onMouseMove={spotlightMove} className="spotlight relative overflow-hidden rounded-[28px] border border-border">
              <img src={APPS_IMG} alt="App development workspace" className="w-full h-[440px] object-cover" />
              <div className="absolute inset-0 bg-ink/20" />
              <div className="absolute bottom-6 left-6 right-6 rounded-2xl bg-background/80 backdrop-blur-md border border-border px-6 py-4">
                <p className="text-sm font-medium">Two apps in active development</p>
                <p className="text-xs text-muted-foreground mt-1">Estimation tooling &amp; a subcontractor network</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ CONTACT ============ */}
      <section id="contact" className="relative py-28 lg:py-40 bg-ink text-background scroll-mt-20 overflow-hidden">
        <div className="absolute inset-0 dotted-grid opacity-15" aria-hidden />
        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-12 grid gap-16 lg:grid-cols-2">
          <Reveal>
            <p className="text-[11px] tracking-[0.28em] uppercase font-semibold text-accent-warm">/ Contact</p>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-[4.5rem] leading-[0.95] tracking-tighter font-medium">
              Let's start a conversation.
            </h2>
            <p className="mt-6 text-background/65 leading-relaxed max-w-md">
              Tell us what you need — a solar quote, a service comparison, or a question
              about our apps. We're happy to help.
            </p>
            <div className="mt-10 space-y-4 text-sm">
              <p className="flex items-center gap-3 text-background/80">
                <span className="grid place-items-center h-10 w-10 rounded-full bg-background/10 text-accent-warm"><ShieldCheck className="h-4 w-4" /></span>
                No spam — your details stay private.
              </p>
              <p className="flex items-center gap-3 text-background/80">
                <span className="grid place-items-center h-10 w-10 rounded-full bg-background/10 text-accent-warm"><Clock className="h-4 w-4" /></span>
                Typical response within 1 business day.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <ContactForm />
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
